import {InfoCircleOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {isValidPhoneNumber} from 'libphonenumber-js';
import legalForms from '../../i18n/legal_forms.json';
import {bankingDocumentTypes, localStorageKeysConstants} from '../../constants';
import {EmailRegexp, EUZipRegexp, OtherZipRegexp, TaxRegexp} from '../regexp';
import {cardsHelpers} from './cards.helpers';
import {formHelpers} from './form.helpers';
import {localizationHelpers} from './localization.helpers';
import {objectHelpers} from './object.helpers';
import {
  monthlyIncomeRangeOptions,
  occupationCategoryOptions,
  personalAssetsRangeOptions
} from '../options';

const {DRIVING_LICENSE, IDENTIFY_CARD, PASSPORT} = bankingDocumentTypes;
const {COMPLETE_KYC_BANKING_CARD_DISMISSED} = localStorageKeysConstants;

const {getOptionLabel} = objectHelpers;

const gObjProp = (data, key, defaultValue) => data ? data[key] || defaultValue : defaultValue;

export const EU_POSTCODE_COUNTRIES = ['BE', 'DE', 'ES', 'FR', 'IT'];

/**
 * Collection of KYC (Know Your Customer) form and document utility helpers.
 * @example
 * kycHelpers.getAcceptFileTypes();
 */
export const kycHelpers = {
  /**
   * Returns the accepted file type string for KYC document upload inputs.
   * @returns {string} Comma-separated list of accepted file extensions.
   * @example
   * kycHelpers.getAcceptFileTypes(); // '.doc,.jpg,.jpeg,.png,.pdf'
   */
  getAcceptFileTypes: () => '.doc,.jpg,.jpeg,.png,.pdf',
  /**
   * Returns default Ant Design Select props for country selection fields.
   * @returns {{size: string, showSearch: boolean}} Select component props.
   * @example
   * kycHelpers.getCountrySelectProps(); // { size: 'large', showSearch: true }
   */
  getCountrySelectProps: () => ({
    size: 'large',
    showSearch: true
  }),
  /**
   * Returns Ant Design DatePicker props, adjusting the default picker value for birthday fields.
   * @param {string} propName - Field name; when 'birthday', sets the picker 20 years in the past.
   * @returns {{defaultPickerValue: import('dayjs').Dayjs|undefined, disabledDate: Function, format: string, size: string}} DatePicker props.
   * @example
   * kycHelpers.getDatePickerProps('birthday');
   */
  getDatePickerProps: (propName) => {
    let startDate = dayjs();
    let defaultPickerValue = undefined;
    let minDate = undefined;
    if (propName === 'birthday') {
      startDate = startDate.subtract(20, 'year');
      minDate = dayjs().subtract(100, 'year');
      defaultPickerValue = startDate;
    }
    return {
      defaultPickerValue,
      disabledDate: (current) => current.isAfter(startDate) || (minDate && current.isBefore(minDate)),
      format: 'DD/MM/YYYY',
      size: 'large'
    }
  },
  /**
   * Converts an array of document objects into a form values map keyed by 1-based index.
   * @param {Array<{uploaded: boolean}>} documents - List of KYC document objects.
   * @returns {Object.<string, boolean>} Map of `document-N` keys to their uploaded state.
   * @example
   * kycHelpers.getDocumentsFormValues([{ uploaded: true }]);
   * // { 'document-1': true }
   */
  getDocumentsFormValues: (documents) => {
    let data = {};
    documents.forEach((d, index) => {
      data[`document-${index + 1}`] = d.uploaded;
    });
    return data;
  },
  /**
   * Returns the banking document type ID for a given verify selected type string.
   * @param {string} verifySelectedType - UI document type key ('driving-license', 'id-card', or 'passport').
   * @returns {number|undefined} Banking document type constant, or undefined if not found.
   * @example
   * kycHelpers.getDocumentTypeId('passport'); // bankingDocumentTypes.PASSPORT
   */
  /**
   * Uploads a set of documents one at a time, skipping any the backend has already accepted.
   *
   * Sequential because each upload is its own request against the Lambda payload cap, and
   * checkpointed because the caller may resubmit after a partial failure: without `accepted`, a
   * retry starts from the first document again and re-sends the ones Treezor already has, leaving
   * duplicates on the KYC. Pass a Set the caller owns (a ref) so it survives the resubmit.
   *
   * Each document carries its own type — the legal-person step uploads a company registration and
   * a legal-status document together, while the legal representative uploads one identity card.
   *
   * @param {Object} params
   * @param {Array<{name: string, file: Object, documentTypeId: number}>} params.documents
   * @param {string} params.userId - Banking user the documents belong to.
   * @param {Function} params.createDocument - Callback-style create action (data, onOk, onErr).
   * @param {Set<string>} [params.accepted] - Names already accepted; mutated as each succeeds.
   * @param {Function} [params.onSuccess] - Called once every document has been accepted.
   * @param {Function} [params.onError] - Called with {name, response} for the document that failed.
   * @returns {Promise<boolean>} True when everything was accepted.
   * @example
   * kycHelpers.uploadDocumentsSequentially({documents, userId, createDocument, accepted: ref.current});
   */
  uploadDocumentsSequentially: async ({documents, userId, createDocument, accepted, onSuccess, onError}) => {
    const uploadOne = (payload) => new Promise((resolve, reject) => createDocument(payload, resolve, reject));

    for (const document of documents) {
      if (accepted?.has(document.name)) continue;
      try {
        // Building the payload reads the file, and it is inside the try on purpose: a retry after a
        // rejected upload can arrive with the file object no longer intact, and an exception
        // escaping here left the caller's spinner running forever with no request sent and no error
        // shown. Anything that goes wrong for a document has to surface as that document's error.
        const file = document.file?.originFileObj;
        if (!file) throw new Error('the selected file is no longer available, please re-attach it');
        const payload = await cardsHelpers.getUploadedFilePayload(userId, file, document.documentTypeId);
        await uploadOne(payload);
        accepted?.add(document.name);
      } catch (response) {
        onError?.({name: document.name, response});
        return false;
      }
    }

    onSuccess?.();
    return true;
  },
  getDocumentTypeId: (verifySelectedType) => {
    const documentTypes = {
      'driving-license': DRIVING_LICENSE,
      'id-card': IDENTIFY_CARD,
      'passport': PASSPORT
    }
    return documentTypes[verifySelectedType] || undefined
  },
  /**
   * Returns the UI document type string for a given banking document type ID.
   * @param {number} documentId - Banking document type constant (e.g. DRIVING_LICENSE, PASSPORT, IDENTIFY_CARD).
   * @returns {string|undefined} UI document type key, or undefined if not found.
   * @example
   * kycHelpers.getVerifyDocumentType(bankingDocumentTypes.PASSPORT); // 'passport'
   */
  getVerifyDocumentType: (documentId) => {
    const documentTypes = {
      [DRIVING_LICENSE]: 'driving-license',
      [PASSPORT]: 'passport',
      [IDENTIFY_CARD]: 'id-card'
    }
    return documentTypes[documentId] || undefined
  },
  /**
   * Returns a bundle of localised label/placeholder/validation helpers scoped to a specific KYC form step.
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @param {string} params.formKey - KYC step key used to build the i18n namespace path.
   * @param {string} [params.fullFormKey] - Override the full i18n namespace path, bypassing formKey.
   * @returns {{description: string, info: Function, label: Function, placeholder: Function, lengthFieldMessage: Function, validationPhoneRules: Array, validationPostCodeRules: Array, validationTaxRules: Array, validationRequiredRules: Array, title: string, tooltip: Function}} KYC form helpers bundle.
   * @example
   * kycHelpers.getKYCFormDetails({ t, formKey: 'personalInfo' });
   */
  getKYCFormDetails: ({t, formKey, fullFormKey, form, countryCode}) => {
    const trans = (key, params) => t(`${fullFormKey || `quickStart:kyc.steps.${formKey}`}.${key}`, params);

    const validationRequiredRules = [{required: true, message: t('main:validation.fieldIsRequired')}];
    const lengthFieldMessage = (value, type = 'min') => t(`main:validation.${type}LengthValidation`, {value});

    const nameForbiddenCharsRegexp = /[!"#$%&()*/<=>?@[\\\]_{|}~¡¢£€¥Š§š©ª«¬®¯°±²³µ¶·¹º»¿÷]/;

    const validateNameValue = (value) => {
      if (/\d/.test(value)) return t('main:validation.nameNoDigits');
      if (nameForbiddenCharsRegexp.test(value)) return t('main:validation.nameInvalidChars');
      if (/(.)\1{3,}/.test(value)) return t('main:validation.consecutiveCharsInvalid');
      return null;
    };

    return {
      description: trans(`description`),
      info: (key, params) => trans(`info.${key}`, params),
      label: (key, params) => trans(`labels.${key}`, params),
      placeholder: (key, params) => trans(`placeholders.${key}`, params),
      lengthFieldMessage,
      validationPhoneRules: [
        {
          message: t('auth:validation.phoneIsIncorrect'),
          validator: (_, value) => isValidPhoneNumber(value) ? Promise.resolve() : Promise.reject()
        }
      ],
      validationAddressRules: [
        ...validationRequiredRules,
        {min: 3, message: lengthFieldMessage(4, 'min')},
        {max: 150, message: lengthFieldMessage(150, 'max')},
        {
          message: t('main:validation.consecutiveCharsInvalid'),
          validator: (_, value) => !value || !(/(.)\1{3,}/).test(value) ? Promise.resolve() : Promise.reject()
        }
      ],
      validationEmailRules: [
        ...validationRequiredRules,
        {max: 200, message: lengthFieldMessage(200, 'max')},
        {pattern: EmailRegexp, message: t('main:validation.enterValidEmail')}
      ],
      validationPostCodeRules: [
        ...validationRequiredRules,
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const formCountryCode = (
              countryCode ||
              form?.getFieldValue('country') ||
              form?.getFieldValue('secondaryCountry') ||
              ''
            ).toUpperCase();
            if ((formCountryCode && EU_POSTCODE_COUNTRIES.includes(formCountryCode))) {
              if (!EUZipRegexp.test(value)) {
                return Promise.reject(t('main:validation.zipCodeFiveDigits'));
              }
            } else {
              if (value.length > 20) return Promise.reject(lengthFieldMessage(20, 'max'));
              if (!OtherZipRegexp.test(value)) return Promise.reject(t('main:validation.zipCodeInvalid'));
            }
            return Promise.resolve();
          }
        }
      ],
      validationNameRules: [
        ...validationRequiredRules,
        {max: 150, message: lengthFieldMessage(150, 'max')},
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            if (/,/.test(value)) return Promise.reject(t('main:validation.nameNoComma'));
            const error = validateNameValue(value);
            return error ? Promise.reject(error) : Promise.resolve();
          }
        }
      ],
      validationMiddleNameRules: [
        {max: 150, message: lengthFieldMessage(150, 'max')},
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const parts = value.split(',');
            if (parts.length > 2) return Promise.reject(t('main:validation.middleNameMaxTwo'));
            for (const part of parts) {
              const trimmed = part.trim();
              if (!trimmed) return Promise.reject(t('main:validation.middleNameInvalid'));
              const error = validateNameValue(trimmed);
              if (error) return Promise.reject(error);
            }
            return Promise.resolve();
          }
        }
      ],
      validationTaxRules: [
        ...validationRequiredRules,
        {min: 4, message: lengthFieldMessage(4, 'min')},
        {max: 18, message: lengthFieldMessage(18, 'max')},
        {pattern: TaxRegexp, message: t('main:validation.taxInvalid')}
      ],
      validationRequiredRules,
      title: trans(`title`),
      tooltip: (key) => ({ title: trans(`tooltips.${key}`), icon: <InfoCircleOutlined /> }),
    }
  },
  /**
   * Converts raw form fields for tax residence into the API-expected payload shape.
   * @param {Object} fields - Form field values.
   * @param {string} fields.tax_number - The tax payer identifier (trimmed if string).
   * @param {string} fields.tax_residence - Two-letter country code (uppercased).
   * @returns {{country: string, tax_payer_id: string}} API-ready tax residence payload.
   * @example
   * kycHelpers.getTaxResidenceFormData({ tax_number: ' FR1234 ', tax_residence: 'fr' });
   * // { country: 'FR', tax_payer_id: 'FR1234' }
   */
  getTaxResidenceFormData: (fields) => {
    let taxPayerId = fields.tax_number;
    if (typeof taxPayerId === 'string') taxPayerId = taxPayerId.trim();
    return {
      country: fields.tax_residence.toUpperCase(),
      tax_payer_id: taxPayerId
    }
  },
  /**
   * Returns true when at least one legal form exists for the given country code.
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code.
   * @returns {boolean} True if the country has legal form options defined.
   * @example
   * kycHelpers.isSupportedCountry('FR'); // true
   */
  isSupportedCountry: (countryCode) => {
    let supported = false;
    if (countryCode && typeof(countryCode) === 'string') {
      supported = (legalForms[countryCode.toUpperCase()] || []).length > 0;
    }
    return supported;
  },
  /**
   * Returns true if the user previously dismissed the banking CompleteKycCard.
   * @returns {boolean} Dismissed state.
   * @example
   * kycHelpers.isBankingCardDismissed(); // false
   */
  isBankingCardDismissed: () => Boolean(localStorage.getItem(COMPLETE_KYC_BANKING_CARD_DISMISSED)),
  /**
   * Persists the dismissal of the banking CompleteKycCard so it stays hidden.
   * @returns {void}
   * @example
   * kycHelpers.dismissBankingCard();
   */
  dismissBankingCard: () => localStorage.setItem(COMPLETE_KYC_BANKING_CARD_DISMISSED, '1'),
  /**
   * Returns a list of legal form select options for the given country code, truncating long names.
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code.
   * @returns {Array<{label: string, value: string}>} Legal form select options.
   * @example
   * kycHelpers.getLegalFormOptions('FR');
   */
  getLegalFormOptions: (countryCode) => {
    const items = countryCode ? legalForms[countryCode.toUpperCase()] || [] : [];
    const maxLengthName = 80;
    return items
      .filter(i => i.legal_form_api)
      .map(i => {
        const {legal_form_api: value} = i;
        let label = i.name;
        if (label.length > maxLengthName) {
          label = `${label.substring(0, maxLengthName)}...`;
        }
        return {
          label,
          value
        }
      });
  },
  /**
   * Builds the full KYC invite page URL for a given invite key.
   * @param {string} key - KYC invite key appended as a query parameter.
   * @returns {string} Full URL for the user invite page.
   * @example
   * kycHelpers.getKYCInvitePageUrl('abc123');
   * // 'https://app.example.com/user-invite/?key=abc123'
   */
  getKYCInvitePageUrl: (key) => `${window.location.origin}/user-invite/?key=${key}`,
  /**
   * Constructs an Ant Design form error array for the tax_number field from a server response.
   * @param {Object} response - Server error response object.
   * @param {string} [response.detail] - Error detail message from the server.
   * @param {Function} t - i18n translation function.
   * @returns {Array<{name: string, errors: string[]}>} Form error descriptors for the tax_number field.
   * @example
   * kycHelpers.getTaxNumberFormErrors({ detail: 'Invalid tax number' }, t);
   */
  getTaxNumberFormErrors: (response, t) => {
    const detail = response?.detail;
    let message = null;
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      // A 422 detail is an array of {loc, type, msg}. Handing it straight to antd
      // rendered "[object Object]", so the real reason never reached the user
      // (H1MVP-4977).
      message = detail
        .map(err => [Array.isArray(err?.loc) ? err.loc[err.loc.length - 1] : null, err?.msg]
          .filter(Boolean).join(': '))
        .filter(Boolean)
        .join('; ');
    }
    return [{name: 'tax_number', errors: [message || t('main:serverErrorMessage')]}]
  },
  /**
   * Constructs Ant Design form error descriptors from a user-create API error response.
   * @param {Object} response - Server error response object.
   * @param {string|Array} [response.detail] - Error detail; may be a string or array of {msg} objects.
   * @param {Function} t - i18n translation function.
   * @returns {Array<{name: string, errors: string[]}>} Form error descriptors.
   * @example
   * kycHelpers.getUserCreateFormErrors({ detail: [{ msg: 'Email already exists' }] }, t);
   */
  getUserCreateFormErrors: (response, t) => {
    let formErrors = formHelpers.getFormServerErrorFields(response);
    if (formErrors.length === 0 && typeof (response.detail) === 'object' && response.detail.length > 0) {
      formErrors = [{name: 'email', errors: [response.detail[0].msg || t('main:serverErrorMessage')]}]
    }
    return formErrors;
  },
  /**
   * Returns Ant Design form error descriptors for a file upload field, handling size and server errors.
   * @param {Object} params - Parameters object.
   * @param {string} params.name - Form field name associated with the file input.
   * @param {Object} params.response - Server error response object.
   * @param {Function} params.t - i18n translation function.
   * @returns {Array<{name: string, errors: string[]}>} Form error descriptors, or empty array if no error.
   * @example
   * kycHelpers.getFileFormErrors({ name: 'passport_scan', response, t });
   */
  getFileFormErrors: ({name, response, t}) => {
    if (response?.config?.data && new Blob([response.config.data]).size > 5000000) {
      return [{name, errors: [t('main:supportingImageSizeError')]}];
    }
    const errorMessage = response?.message || response?.detail;
    if (errorMessage) {
      const message = typeof errorMessage === 'object' ? errorMessage[0]?.msg : errorMessage;
      return [{ name, errors: [message || t('main:serverErrorMessage')] }];
    }
    return [];
  },
  /**
   * Enables or disables the submit button on the legal representative confirmation form based on checkbox state.
   * @param {Object} params - Parameters object.
   * @param {Object} params.allValues - All current form field values.
   * @param {Object} params.changedFields - Map of recently changed field keys to their new values.
   * @param {Function} [params.handleEnableSubmit] - Callback to enable/disable the submit button.
   * @returns {void}
   * @example
   * kycHelpers.handleOnValuesChangeLRForm({ allValues, changedFields, handleEnableSubmit: setEnabled });
   */
  handleOnValuesChangeLRForm: ({allValues, changedFields, handleEnableSubmit}) => {
    const confirmFields = ['confirm_seen', 'confirm_accept'];
    const isChangedConfirmField = Object.keys(changedFields).some(item => confirmFields.includes(item))
    if (isChangedConfirmField && handleEnableSubmit) {
      const enable = confirmFields.map(field => allValues[field]).every(v => v === true);
      handleEnableSubmit(enable);
    }
  },
  /**
   * Returns the default form values for an employee invite/edit form.
   * @param {Object|null|undefined} employee - Employee object, or falsy if not yet loaded.
   * @param {string} [employee.email] - Employee's email address.
   * @returns {{email?: string}} Default form values object.
   * @example
   * kycHelpers.getEmployeeDefaultFormValues({ email: 'jane@example.com' });
   * // { email: 'jane@example.com' }
   */
  getEmployeeDefaultFormValues: (employee) => {
    let defaultFormValues = {};
    if (employee) {
      defaultFormValues = {
        email: gObjProp(employee, 'email'),
      }
    }
    return defaultFormValues;
  },
  /**
   * Returns a localised tax code label optionally suffixed with the country-specific tax code name.
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @param {string} params.taxResidence - ISO country code of the tax residence.
   * @returns {string} Localised tax code label.
   * @example
   * kycHelpers.getTaxCodeLabelByCountryCode({ t, taxResidence: 'FR' });
   * // 'Tax Code - SIREN'
   */
  getTaxCodeLabelByCountryCode: ({t, taxResidence}) => {
    let label = t('main:taxCode');
    const taxCodeCountryLabel = localizationHelpers.getTaxCodeByCountry(taxResidence);
    if (taxCodeCountryLabel) label = `${label} - ${taxCodeCountryLabel}`;
    return label;
  },
  /**
   * Returns the display label for an occupation category option value.
   * @param {string} value - Occupation category option value.
   * @returns {string|undefined} Matching label, or undefined if not found.
   * @example
   * kycHelpers.getOccupationCategory('employee'); // 'Employee'
   */
  getOccupationCategory: (value) => getOptionLabel(occupationCategoryOptions, value),
  /**
   * Returns the display label for a monthly income range option value.
   * @param {string} value - Monthly income range option value.
   * @returns {string|undefined} Matching label, or undefined if not found.
   * @example
   * kycHelpers.getMonthlyIncomeRange('1000-3000'); // '1,000 – 3,000 €'
   */
  getMonthlyIncomeRange: (value) => getOptionLabel(monthlyIncomeRangeOptions, value),
  /**
   * Returns the display label for a personal assets range option value.
   * @param {string} value - Personal assets range option value.
   * @returns {string|undefined} Matching label, or undefined if not found.
   * @example
   * kycHelpers.getPersonalAssetsRangeLabel('50000-100000'); // '50,000 – 100,000 €'
   */
  getPersonalAssetsRangeLabel: (value) => getOptionLabel(personalAssetsRangeOptions, value),
}
